/**
 * Entidade de domínio que espelha uma entrada do registro central de plugin
 * por tipo (HUB-237, ver ADR-010), resolvida via
 * `GET /plugin-registry/{activation_type}` logo após o carregamento da
 * config do evento (CA-01/CA-12).
 *
 * `isReady=false` preserva o caminho de ativação fake existente
 * (`FakeLeadCaptureForm`) — decisão central da spec para não regredir o
 * piloto enquanto `plugin-quiz`/`plugin-roleta` ainda são stubs (CA-02).
 */
export interface PluginRegistryEntry {
  readonly activationType: string;
  readonly remoteUrl: string;
  readonly moduleName: string;
  readonly version: string;
  readonly isReady: boolean;
}

/** Erro de schema inesperado na resposta de `GET /plugin-registry/{type}` —
 * nunca deve travar em tela branca; quem chama trata como falha genérica
 * (mesma classe de erro de `InvalidEventConfigSchemaError`, HUB-235). */
export class InvalidPluginRegistrySchemaError extends Error {
  constructor() {
    super('Registro de plugin em formato inesperado.');
    this.name = 'InvalidPluginRegistrySchemaError';
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function createPluginRegistryEntry(raw: unknown): PluginRegistryEntry {
  if (!isPlainObject(raw)) {
    throw new InvalidPluginRegistrySchemaError();
  }

  const remoteUrl = raw['remote_url'];
  const moduleName = raw['module_name'];
  const version = raw['version'];
  if (typeof remoteUrl !== 'string' || typeof moduleName !== 'string' || typeof version !== 'string') {
    throw new InvalidPluginRegistrySchemaError();
  }

  return {
    activationType: String(raw['activation_type'] ?? ''),
    remoteUrl,
    moduleName,
    version,
    isReady: raw['is_ready'] === true,
  };
}
